import LineChart from "common/Charts/LineChart/LineChart";
import React, { CSSProperties, MouseEvent, useEffect, useState } from "react";
import { FormattedChartDataPoint } from "@shared/types";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import styled, { useTheme } from "styled-components";
import {
  BASE_CHART_TIME_RANGES,
  TIME_RANGE_TO_SECONDS,
} from "@shared/common/constants";
import { hexToRGB } from "@shared/utils/misc";
import { DISPLAY_CURRENCIES } from "common/constants";
import { getEmptyGraphData } from "common/Charts/utils";
import BarChart from "common/Charts/BarChart.tsx";
import Tabs from "common/Tabs/Tabs.tsx";

interface ExpandingChartProps {
  graphData?: FormattedChartDataPoint[];
  tabsItems?: {
    key: string;
    label: string;
    data: FormattedChartDataPoint[];
    isBarChart?: boolean;
  }[];
  selectedTimeRange: string;
  setSelectedTimeRange: (timeRange: string) => void;
  precision?: number;
  loading: boolean;
  isSuccess?: boolean;
  expanded?: boolean;
  isBarChart?: boolean;
  expandedChartHeight?: number;
  hideXAxis?: boolean;
  axisOverlapData?: boolean;
  usePositiveNegativeColorScheme?: boolean;
}

const ExpandingChart = ({
  graphData,
  tabsItems,
  selectedTimeRange,
  setSelectedTimeRange,
  precision,
  loading,
  isSuccess,
  expanded,
  isBarChart,
  expandedChartHeight = EXPANDED_CHART_HEIGHT,
  hideXAxis,
  axisOverlapData,
  usePositiveNegativeColorScheme,
}: ExpandingChartProps) => {
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const chartHeight = expanded ? expandedChartHeight : COLLAPSED_CHART_HEIGHT;
  const chartWrapperHeight =
    expanded && !!tabsItems ? chartHeight + TABS_HEIGHT : chartHeight;
  const [tab, setTab] = useState<string>();

  useEffect(() => {
    if (!!tabsItems && !tab) {
      setTab(tabsItems[0].key);
    }
  }, [tabsItems]);

  const chartProps = {
    displayCurrency,
    style: {
      position: "relative",
      paddingLeft: axisOverlapData || !expanded ? 0 : 8,
    } as CSSProperties,
    loading,
    loadingProps: {
      backgroundColor: "transparent",
      spinnerColor: theme.isNewLook
        ? theme.colors.primaryBlue
        : theme.colors.white,
    },
    precision: displayCurrency === DISPLAY_CURRENCIES.USD ? 0 : precision,
    isSuccess,
    isWebsocket: true,
    hideXAxis: hideXAxis || !expanded,
    color: theme.isNewLook ? theme.colors.primaryBlue : theme.colors.white,
    ticksColor: theme.isNewLook
      ? theme.colors.secondaryText
      : hexToRGB(theme.colors.white, 0.2),
    numOfYTicks: 4,
    numOfXTicks: 4,
    axisOverlapData: axisOverlapData || !expanded,
    gridColor: theme.isNewLook
      ? theme.colors.chartGridColor
      : hexToRGB(theme.colors.primaryGrey, 0.2),
    lineWidth: 2.5,
    includeBounds: true,
    height: chartHeight,
  };

  const getDisplayedGraphData = (graphData?: FormattedChartDataPoint[]) => {
    return !graphData?.length && !loading
      ? getEmptyGraphData(TIME_RANGE_TO_SECONDS[selectedTimeRange])
      : graphData;
  };

  const getChart = (
    graphData?: FormattedChartDataPoint[],
    isBarChart?: boolean
  ) => {
    const data = getDisplayedGraphData(graphData);
    return isBarChart ? (
      <BarChart
        {...chartProps}
        data={data}
        positiveColor={theme.isNewLook ? theme.colors.green : "#79FF9F"}
        negativeColor={theme.isNewLook ? theme.colors.red : "#FF7A7A"}
        usePositiveNegativeColorScheme={usePositiveNegativeColorScheme}
      />
    ) : (
      <LineChart {...chartProps} hideGradientBg={true} data={data} />
    );
  };

  let chart: React.JSX.Element;
  if (!!tabsItems) {
    if (expanded) {
      tabsItems = tabsItems.map((tabItem) => {
        return {
          ...tabItem,
          children: getChart(tabItem.data, tabItem.isBarChart),
        };
      });
      chart = (
        <StyledTabs
          activeKey={tab}
          onTabClick={(tabKey: string) => setTab(tabKey)}
          size="small"
          tabBarStyle={{ margin: "0 0 8px 0" }}
          $hideBorder={true}
          items={tabsItems}
        />
      );
    } else {
      const selectedTabData = tabsItems.find((tabItem) => tabItem.key === tab);
      chart = getChart(selectedTabData?.data, selectedTabData?.isBarChart);
    }
  } else {
    chart = getChart(graphData, isBarChart);
  }

  return (
    <div style={{ paddingTop: expanded ? 16 : 0, position: "relative" }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ height: chartWrapperHeight }}
      >
        {chart}
      </div>
      {expanded && (
        <TimeRangeButtonsWrapper>
          {BASE_CHART_TIME_RANGES.map((timeRange, index) => {
            return (
              <TimeRangeButton
                key={index}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  setSelectedTimeRange(timeRange);
                }}
                $selected={timeRange === selectedTimeRange}
              >
                {timeRange.toUpperCase()}
              </TimeRangeButton>
            );
          })}
        </TimeRangeButtonsWrapper>
      )}
    </div>
  );
};

export default ExpandingChart;

export const TIME_RANGE_BUTTONS_HEIGHT = 38;
export const EXPANDED_CHART_HEIGHT = 170;
export const COLLAPSED_CHART_HEIGHT = 120;
export const TABS_HEIGHT = 42;

const StyledTabs = styled(Tabs)`
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,
  .ant-tabs-tab-btn {
    color: ${(p) =>
      p.theme.isNewLook
        ? p.theme.colors.primaryBlue
        : p.theme.colors.white} !important;
  }
  .ant-tabs-ink-bar {
    background: ${(p) =>
      p.theme.isNewLook
        ? p.theme.colors.primaryBlue
        : p.theme.colors.white} !important;
  }
  &.ant-tabs .ant-tabs-tab {
    padding: 2px 0 !important;
    &:not(:first-child) {
      margin-left: 16px;
    }
    @media (max-width: 375px) {
      font-size: 13px;
    }
    @media (max-width: 350px) {
      font-size: 12px;
    }
  }
  &.ant-tabs > .ant-tabs-nav .ant-tabs-nav-wrap {
    padding: 0 16px 8px;
  }
`;
const TimeRangeButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 8px 16px 0;
`;
const TimeRangeButton = styled.div<{ $selected?: boolean }>`
  font-size: ${(p) => p.theme.typography.fontSizes.S};
  color: ${(p) =>
    p.theme.isNewLook ? p.theme.colors.primaryBlue : p.theme.colors.white};
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  padding: 4px 12px;
  border-radius: 8px;
  border: 1px solid
    ${(p) =>
      p.theme.isNewLook ? p.theme.colors.primaryBlue : p.theme.colors.white};
  border-width: ${(p) => (p.$selected ? 1 : 0)};
`;
